import React, { useState, useEffect, useMemo } from "react";
import styles from "./PerfilMain.module.scss";
import { getPortfolio } from "../../../store/actions/registerActions";
import styled from "styled-components";
import { connect } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileEditModalNew from "../../UI/ProfileEditModal/ProfileEditModalNew";
import AccountSettingsModal from "../../UI/AccountSettingsModal/AccountSettingsModal.tsx";
import ProfilePostSuggestionModal from "../../UI/ProfilePostSuggestionModal/ProfilePostSuggestionModal.tsx";
import NavBar from "./NavBar/NavBar.tsx";
import ProfileOportunidades from "./ProfileOportunidades/ProfileOportunidades.tsx";
import ProfileGestion from "./ProfileGestion/ProfileGestion.tsx";
import ProfileMuro from "./ProfileMuro/ProfileMuro.tsx";
import muroStyles from "./ProfileMuro/ProfileMuro.module.scss";
import UserInfo from "../PerfilMain/UserInfo/UserInfo";
import MisComentarios from "./MisComentarios/MisComentarios";
import MisProductos from "./MisProductos/MisProductos";
import Resumen from "./Resumen/Resumen.tsx";
import ProfileCommerceMaterialsPublic from "./Resumen/ProfileCommerceMaterialsPublic.tsx";
import { PackagePlus, PenSquare, Sparkles, Star } from "lucide-react";
import { useActionGuard } from "../../../hooks/useActionGuard.ts";
import { openCalificationModal } from "../../../store/actions";
import Breadcrumbs from "../../UI/Breadcrumbs/Breadcrumbs.tsx";
import {
  getProfileTabFromQuery,
  buildProfileUrl,
  getProfilePathInfo,
  getUserDisplayName,
  resolveProfileTab,
  getGestionSectionFromQuery,
  type ProfileGestionSection,
} from "../../../utils/navigationHelpers.ts";
import { normalizeUserCommerces } from "../../../utils/userCommerces.ts";

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  padding-right: 2%;
  padding-left: 2%;
  max-width: 150rem;
  margin: auto;

  @media (max-width: 900px) {
    margin-top: 80px;
    padding-right: 2rem;
    padding-left: 2rem;
  }

  @media (max-width: 600px) {
    flex-direction: column;
    margin-top: 80px;
    padding-right: 2rem;
    padding-left: 2rem;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  gap: 2rem;

  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
    padding-top: 0;
  }
`;

type ProfileTabName =
  | "Resumen"
  | "Materiales"
  | "Productos/Servicios"
  | "Oportunidades"
  | "Calificaciones"
  | "Muro"
  | "Gestión";

type OportunidadesSection = "proyectos" | "materiales";

interface UserProfileData {
  id?: string | number;
  type?: string;
  name?: string;
  last_name?: string;
  razon_social?: string;
  business_name?: string;
  [key: string]: unknown;
}

interface PerfilMainStateProps {
  perfilAjeno?: string | number;
  userData?: UserProfileData;
}

interface PerfilMainOwnProps {
  perfilAjeno?: string | number;
  profileVisualizerData?: UserProfileData | null;
  myPerson?: boolean;
}

interface PerfilMainDispatchProps {
  getPortfolio: (userId?: string | number) => void;
  openCalificationModal: (user: UserProfileData | null) => void;
}

type PerfilMainProps = PerfilMainStateProps &
  PerfilMainOwnProps &
  PerfilMainDispatchProps;

const PerfilMain = ({
  userData,
  myPerson,
  perfilAjeno,
  profileVisualizerData,
  openCalificationModal,
}: PerfilMainProps) => {
  const { guard } = useActionGuard();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const profilePathInfo = useMemo(
    () => getProfilePathInfo(location.pathname),
    [location.pathname]
  );
  const [createNewPortfolio, setCreateNewPortfolio] = useState(false);

  const tabFromQuery = getProfileTabFromQuery(searchParams);
  const [isActive, setIsActive] = useState<ProfileTabName | string>(
    tabFromQuery || "Resumen"
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [profilePostModal, setProfilePostModal] = useState<{
    open: boolean;
    mode: "ai_suggested" | "composer";
  }>({ open: false, mode: "ai_suggested" });
  const [muroRefreshToken, setMuroRefreshToken] = useState(0);
  const [oportunidadesSection, setOportunidadesSection] =
    useState<OportunidadesSection>("proyectos");
  const [, setInfoReady] = useState(false);

  const visitorCommerceRefs = useMemo(
    () => (!myPerson ? normalizeUserCommerces(profileVisualizerData ?? {}) : []),
    [myPerson, profileVisualizerData]
  );
  const showMaterialesTab = !myPerson && visitorCommerceRefs.length > 0;

  const gestionSection = useMemo((): ProfileGestionSection => {
    const s = getGestionSectionFromQuery(searchParams);
    return s ?? "cotizaciones";
  }, [searchParams]);

  const navigateProfilePreserving = (baseUrl: string) => {
    const preserveKeys = [
      "from",
      "skill",
      "jurisdictions",
      "allCountry",
      "page",
    ];
    const extraParams = new URLSearchParams();
    preserveKeys.forEach((key) => {
      const value = searchParams.get(key);
      if (value) {
        extraParams.set(key, value);
      }
    });
    const extraQuery = extraParams.toString();
    const newUrl = extraQuery
      ? `${baseUrl}${baseUrl.includes("?") ? "&" : "?"}${extraQuery}`
      : baseUrl;
    navigate(newUrl, { replace: true });
  };

  const resolveProfileContext = () => {
    const currentProfileUser = myPerson ? userData : profileVisualizerData;
    const fallbackUserId = myPerson ? userData?.id : perfilAjeno;
    const profileUserIdFromPath = profilePathInfo.userId;
    const profileUserNameFromPath = profilePathInfo.userName;
    const resolvedUserId =
      currentProfileUser?.id || fallbackUserId || profileUserIdFromPath;
    const resolvedDisplayName =
      getUserDisplayName(currentProfileUser) ||
      profileUserNameFromPath ||
      (myPerson ? getUserDisplayName(userData) : null) ||
      "perfil";
    return { resolvedUserId, resolvedDisplayName };
  };

  useEffect(() => {
    const tab = getProfileTabFromQuery(searchParams);
    if (tab) {
      setIsActive(tab);
    }
  }, [location.search, searchParams]);

  useEffect(() => {
    if (isActive !== "Oportunidades") {
      setOportunidadesSection("proyectos");
    }
  }, [isActive]);

  useEffect(() => {
    const raw = searchParams.get("tab");
    if (!raw) return;
    if (resolveProfileTab(raw)) return;
    const { resolvedUserId, resolvedDisplayName } = resolveProfileContext();
    if (resolvedUserId && resolvedDisplayName) {
      navigateProfilePreserving(
        buildProfileUrl(
          String(resolvedUserId),
          resolvedDisplayName,
          "resumen"
        )
      );
    }
  }, [location.search, searchParams, myPerson, userData, profileVisualizerData, perfilAjeno, profilePathInfo.userId, profilePathInfo.userName]);

  useEffect(() => {
    const tab = getProfileTabFromQuery(searchParams);
    if (tab !== "Gestión" || myPerson) return;
    const { resolvedUserId, resolvedDisplayName } = resolveProfileContext();
    if (resolvedUserId && resolvedDisplayName) {
      navigateProfilePreserving(
        buildProfileUrl(
          String(resolvedUserId),
          resolvedDisplayName,
          "resumen"
        )
      );
    }
  }, [location.search, searchParams, myPerson, userData, profileVisualizerData, perfilAjeno, profilePathInfo.userId, profilePathInfo.userName]);

  useEffect(() => {
    if (myPerson && isActive === "Materiales") {
      const { resolvedUserId, resolvedDisplayName } = resolveProfileContext();
      if (resolvedUserId && resolvedDisplayName) {
        navigateProfilePreserving(
          buildProfileUrl(String(resolvedUserId), resolvedDisplayName, "resumen")
        );
      }
    }
  }, [isActive, myPerson, location.search, searchParams, userData, profileVisualizerData, perfilAjeno, profilePathInfo.userId, profilePathInfo.userName]);

  useEffect(() => {
    if (myPerson) return;
    if (isActive !== "Materiales") return;
    if (visitorCommerceRefs.length > 0) return;
    const { resolvedUserId, resolvedDisplayName } = resolveProfileContext();
    if (resolvedUserId && resolvedDisplayName) {
      navigateProfilePreserving(
        buildProfileUrl(String(resolvedUserId), resolvedDisplayName, "resumen")
      );
    }
  }, [
    isActive,
    myPerson,
    visitorCommerceRefs.length,
    location.search,
    searchParams,
    userData,
    profileVisualizerData,
    perfilAjeno,
    profilePathInfo.userId,
    profilePathInfo.userName,
  ]);

  useEffect(() => {
    if (!myPerson) return;
    const tabParam = resolveProfileTab(searchParams.get("tab"));
    if (tabParam !== "gestion") return;
    if (searchParams.get("section") === "materiales_comercio") {
      const q = new URLSearchParams();
      const cid = searchParams.get("commerceId");
      if (cid) q.set("commerceId", cid);
      const from = searchParams.get("from");
      if (from === "perfil") {
        q.set("from", "perfil");
        q.set("tab", "gestion");
        q.set("section", "comercio");
        const uid = searchParams.get("userId");
        const un = searchParams.get("userName");
        if (uid) q.set("userId", uid);
        if (un) q.set("userName", un);
      }
      navigate(`/comercios/catalogo?${q.toString()}`, { replace: true });
      return;
    }
    const sec = getGestionSectionFromQuery(searchParams);
    if (sec) return;
    const { resolvedUserId, resolvedDisplayName } = resolveProfileContext();
    if (resolvedUserId && resolvedDisplayName) {
      navigateProfilePreserving(
        buildProfileUrl(
          String(resolvedUserId),
          resolvedDisplayName,
          "Gestión",
          { section: "cotizaciones" }
        )
      );
    }
  }, [
    location.search,
    searchParams,
    myPerson,
    navigate,
    userData,
    profileVisualizerData,
    perfilAjeno,
    profilePathInfo.userId,
    profilePathInfo.userName,
  ]);

  useEffect(() => {
    setInfoReady(false);
  }, [isActive]);

  const handleTabChange = (tabName: string) => {
    setIsActive(tabName);
    const { resolvedUserId, resolvedDisplayName } = resolveProfileContext();

    if (resolvedUserId && resolvedDisplayName) {
      const cidRaw = searchParams.get("commerceId");
      const cidNum =
        cidRaw != null && cidRaw !== "" && Number.isFinite(Number(cidRaw))
          ? Number(cidRaw)
          : null;
      const profileOptions =
        tabName === "Gestión"
          ? { section: "cotizaciones" as ProfileGestionSection }
          : tabName === "Materiales" && cidNum != null
            ? { commerceId: cidNum }
            : undefined;
      const baseUrl = buildProfileUrl(
        String(resolvedUserId),
        resolvedDisplayName,
        tabName,
        profileOptions
      );
      navigateProfilePreserving(baseUrl);
    }
  };

  const handleGestionSectionChange = (section: ProfileGestionSection) => {
    const { resolvedUserId, resolvedDisplayName } = resolveProfileContext();
    if (resolvedUserId && resolvedDisplayName) {
      navigateProfilePreserving(
        buildProfileUrl(
          String(resolvedUserId),
          resolvedDisplayName,
          "Gestión",
          { section }
        )
      );
    }
  };

  const handleNewPortfolioClick = (value: boolean) => {
    setCreateNewPortfolio(value);
  };

  const renderSection = (): React.ReactNode => {
    switch (isActive) {
      case "Resumen":
        return (
          <Resumen
            myPerson={myPerson}
            userData={userData}
            profileVisualizerData={profileVisualizerData}
            perfilAjeno={perfilAjeno}
            setInfoReady={setInfoReady}
          />
        );
      case "Materiales":
        if (visitorCommerceRefs.length === 0) {
          return null;
        }
        return (
          <div className={styles.sectionContent}>
            <ProfileCommerceMaterialsPublic commerces={visitorCommerceRefs} />
          </div>
        );
      case "Productos/Servicios":
        return (
          <div className={styles.sectionContent}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Productos y servicios</h2>
              {myPerson && (
                <button
                  className={styles.sectionPrimaryButton}
                  onClick={() => handleNewPortfolioClick(true)}
                >
                  <PackagePlus size={20} />
                  Crear nuevo
                </button>
              )}
            </div>
            <MisProductos
              createNewPortfolio={createNewPortfolio}
              perfilAjeno={perfilAjeno}
              myPerson={myPerson}
              handleNewPortfolioClick={handleNewPortfolioClick}
              userData={userData}
              setInfoReady={setInfoReady}
              currentTab={isActive}
            />
          </div>
        );
      case "Calificaciones":
        return (
          <div className={styles.sectionContent}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Calificaciones y reseñas</h2>
              {!myPerson && (
                <button
                  className={styles.sectionPrimaryButton}
                  onClick={() =>
                    openCalificationModal(profileVisualizerData || null)
                  }
                >
                  <Star size={20} />
                  Calificar
                </button>
              )}
            </div>
            <MisComentarios
              perfilAjeno={perfilAjeno}
              myPerson={myPerson}
              setInfoReady={setInfoReady}
            />
          </div>
        );
      case "Oportunidades":
        return (
          <ProfileOportunidades
            section={oportunidadesSection}
            onSectionChange={setOportunidadesSection}
            myPerson={myPerson}
            perfilAjeno={perfilAjeno}
            userData={userData}
            profileVisualizerData={profileVisualizerData}
            setInfoReady={setInfoReady}
          />
        );
      case "Muro":
        return (
          <div className={styles.sectionContent}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Muro</h2>
              {myPerson && (
                <div className={muroStyles.muroHeaderActions}>
                  <button
                    type="button"
                    className={muroStyles.aiSuggestButton}
                    onClick={guard("generate_post", () =>
                      setProfilePostModal({ open: true, mode: "ai_suggested" })
                    )}
                    title="Generar un post con ayuda de IA a partir de tu perfil"
                  >
                    <Sparkles size={18} strokeWidth={2} />
                    Generar post
                  </button>
                  <button
                    type="button"
                    className={styles.sectionPrimaryButton}
                    onClick={guard("create_post", () =>
                      setProfilePostModal({ open: true, mode: "composer" })
                    )}
                    title="Publicar un posteo en tu muro"
                  >
                    <PenSquare size={20} />
                    Nuevo post
                  </button>
                </div>
              )}
            </div>
            <ProfileMuro
              myPerson={myPerson}
              perfilAjeno={perfilAjeno}
              userData={userData}
              profileVisualizerData={profileVisualizerData}
              setInfoReady={setInfoReady}
              refreshToken={muroRefreshToken}
            />
          </div>
        );
      case "Gestión":
        if (!myPerson) {
          return null;
        }
        return (
          <ProfileGestion
            section={gestionSection}
            onSectionChange={handleGestionSectionChange}
            perfilAjeno={perfilAjeno}
            userData={userData}
            profileVisualizerData={profileVisualizerData}
            setInfoReady={setInfoReady}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Container>
      <Wrapper>
        <div className={styles.breadcrumbContainer}>
          <Breadcrumbs />
        </div>
        <div className={styles.profileHeaderContainer}>
          <UserInfo
            setIsEditing={setIsEditing}
            setIsSettingsOpen={setIsSettingsOpen}
            profileVisualizerData={profileVisualizerData}
            myPerson={myPerson}
          />
        </div>
        <div className={styles.profileSurface}>
          <div className={styles.profileSurfaceNav}>
            <NavBar
              myPerson={myPerson}
              setIsActive={handleTabChange}
              setInfoReady={setInfoReady}
              isActive={isActive}
              embedded
              showMaterialesTab={showMaterialesTab}
            />
          </div>
          <div className={styles.profileSurfaceBody}>{renderSection()}</div>
        </div>
        {isEditing && (
          <ProfileEditModalNew setIsEditing={setIsEditing} userData={userData} />
        )}
        {isSettingsOpen && (
          <AccountSettingsModal
            isOpen={isSettingsOpen}
            onClose={() => setIsSettingsOpen(false)}
            setInfoReady={setInfoReady}
          />
        )}
        {profilePostModal.open && (
          <ProfilePostSuggestionModal
            isOpen={profilePostModal.open}
            mode={profilePostModal.mode}
            onClose={() =>
              setProfilePostModal((prev) => ({ ...prev, open: false }))
            }
            userData={userData}
            onPublish={
              profilePostModal.mode === "composer"
                ? () => setMuroRefreshToken((t) => t + 1)
                : undefined
            }
          />
        )}
      </Wrapper>
    </Container>
  );
};

interface RootState {
  general?: {
    userData?: UserProfileData;
    [key: string]: unknown;
  };
}

const mapStateToProps = (
  state: RootState,
  ownProps: PerfilMainOwnProps
): PerfilMainStateProps => ({
  perfilAjeno: ownProps.perfilAjeno,
  userData: state.general?.userData,
});

export default connect(mapStateToProps, {
  getPortfolio,
  openCalificationModal,
})(PerfilMain);
